import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ServicesLinks } from '../data/servicesData';
import { FaArrowRight } from 'react-icons/fa';

/* ── Intersection Observer hook ─────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Services() {
  const [heroRef, heroVisible] = useInView(0.1);
  const [gridRef, gridVisible] = useInView(0.1);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ────────────────────────────────────────── */}
      <section ref={heroRef} className="pt-32 pb-16 px-6" style={{ background: 'var(--bg-surface)' }}>
        <div className="section-container text-center">
          <p className={`eyebrow mb-4 anim-hidden ${heroVisible ? 'anim-visible' : ''}`}>Our Services</p>
          <h1 className={`display-lg mb-6 anim-hidden anim-delay-1 ${heroVisible ? 'anim-visible' : ''}`}
              style={{ color: 'var(--text-primary)' }}>
            Solutions that <span className="gradient-text">Drive Growth</span>
          </h1>
          <p className={`subheadline max-w-2xl mx-auto anim-hidden anim-delay-2 ${heroVisible ? 'anim-visible' : ''}`}>
            From robust web applications to targeted email marketing campaigns, 
            we provide end-to-end digital services tailored to your specific business goals.
          </p>
        </div>
      </section>

      {/* ── SERVICES GRID ───────────────────────────────────────── */}
      <section ref={gridRef} className="section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ServicesLinks.map((service, i) => (
              <div 
                key={service.id || i}
                className={`cyber-card group relative p-8 
                           card-hover overflow-hidden flex flex-col h-full
                           anim-hidden anim-delay-${Math.min(i + 1, 6)} ${gridVisible ? 'anim-visible' : ''}`}
                style={{ border: '1px solid var(--border-color)' }}
              >
                {/* Hover gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 
                                 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                
                <div className="relative z-10 flex flex-col h-full">
                  <div className={`w-14 h-14 rounded-2xl ${service.iconBg} flex items-center justify-center mb-6 
                                  transition-transform duration-500 group-hover:scale-110`}>
                    <service.icon size={24} />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 tracking-tight"
                      style={{ color: 'var(--text-primary)' }}>{service.title}</h3>
                  <p className="text-sm leading-relaxed mb-6 flex-grow text-left"
                     style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>

                  {/* Tech tags list */}
                  {service.tags && (
                    <div className="flex flex-wrap gap-1.5 mb-5 pt-3 border-t w-full" style={{ borderColor: 'var(--section-divider)' }}>
                      {service.tags.map((tag, idx) => (
                        <span 
                          key={idx} 
                          className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md"
                          style={{ 
                            background: 'var(--bg-primary)', 
                            border: '1px solid var(--border-color)',
                            color: 'var(--text-tertiary)' 
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-auto w-full text-left">
                    <Link 
                      to={service.url}
                      className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                      style={{ color: 'var(--accent)' }}
                    >
                      Explore Service <FaArrowRight size={12} className="transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ─────────────────────────────────────────── */}
      <section className="section-padding-sm"
               style={{ 
                 background: 'var(--bg-card)',
                 borderTop: '1px solid var(--section-divider)',
               }}>
        <div className="section-container text-center">
          <h2 className="headline mb-4" style={{ color: 'var(--text-primary)' }}>
            Not sure which service you need?
          </h2>
          <p className="subheadline max-w-lg mx-auto mb-8">
            Let's discuss your project requirements and find the perfect technical solution.
          </p>
          <Link to="/contactus" className="btn-primary">
            Schedule a Consultation <FaArrowRight size={11} />
          </Link>
        </div>
      </section>

    </div>
  );
}
